import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import AppLayout from '@/components/layout/AppLayout';
import { Plus, FolderOpen, MagnifyingGlass, Notebook, UsersThree } from '@phosphor-icons/react';
import { CaseCard } from './dashboard/CaseCard';
import { CreateCaseModal } from './dashboard/CreateCaseModal';
import { TemplatesModal } from './dashboard/TemplatesModal';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [sharedCases, setSharedCases] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [newCase, setNewCase] = useState({ title: '', description: '', case_type: 'other' });
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('my');

  const loadAll = useCallback(async () => {
    try {
      const [cRes, sRes, tRes] = await Promise.all([
        api.get('/cases'),
        api.get('/cases/shared').catch(() => ({ data: { cases: [] } })),
        api.get('/templates'),
      ]);
      setCases(cRes.data.cases);
      setSharedCases(sRes.data.cases);
      setTemplates(tRes.data.templates);
    } catch (err) {
      logger.error('Dashboard load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createCase = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post('/cases', newCase);
      navigate(`/cases/${data.case._id}`);
    } catch (err) {
      logger.error('Create case failed:', err);
      alert(err.response?.data?.detail || 'Failed to create case');
    } finally {
      setCreating(false);
    }
  };

  const createFromTemplate = async (templateId) => {
    setCreating(true);
    try {
      const { data } = await api.post(`/cases/from-template/${templateId}`, {});
      navigate(`/cases/${data.case._id}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create from template');
    } finally {
      setCreating(false);
      setShowTemplates(false);
    }
  };

  const displayCases = tab === 'shared' ? sharedCases : cases;
  const filtered = displayCases.filter(c =>
    c.title.toLowerCase().includes(search.toLowerCase()) || c.case_type?.toLowerCase().includes(search.toLowerCase())
  );

  let emptyHeading = 'No cases yet';
  if (search) emptyHeading = 'No matching cases';
  else if (tab === 'shared') emptyHeading = 'No shared cases';

  const renderCaseList = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1,2,3].map(i => <div key={i} className="h-48 bg-white border border-[#E5E7EB] rounded-lg animate-pulse" />)}
        </div>
      );
    }
    if (filtered.length === 0) {
      return (
        <div className="text-center py-20">
          <FolderOpen size={56} className="mx-auto text-[#E5E7EB] mb-4" />
          <h3 className="font-outfit text-xl font-semibold text-[#09090B] mb-2">{emptyHeading}</h3>
          <p className="text-[#6B7280] font-ibm mb-6">{search ? 'Try a different search term' : 'Create your first investigation case or start from a template'}</p>
          {!search && tab === 'my' && (
            <div className="flex gap-3 justify-center">
              <button onClick={() => setShowTemplates(true)} className="px-5 py-2.5 border border-[#E5E7EB] text-[#09090B] font-ibm font-semibold rounded-md hover:bg-[#F3F4F6] transition-all">Use Template</button>
              <button onClick={() => setShowCreate(true)} className="px-5 py-2.5 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] transition-all">Create Blank Case</button>
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map(c => <CaseCard key={c._id} c={c} />)}
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="p-6 sm:p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-[#09090B]">Investigation Cases</h1>
            <p className="text-[#6B7280] font-ibm mt-1">{cases.length} case{cases.length !== 1 ? 's' : ''}{sharedCases.length > 0 ? ` + ${sharedCases.length} shared` : ''}</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="template-button" onClick={() => setShowTemplates(true)}
              className="flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] text-[#09090B] font-ibm font-semibold rounded-md hover:bg-[#F3F4F6] transition-all text-sm">
              <Notebook size={16} /> Templates
            </button>
            <button data-testid="create-case-button" onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#002FA7] text-white font-ibm font-semibold rounded-md hover:bg-[#00237D] active:scale-[0.98] transition-all text-sm">
              <Plus size={16} weight="bold" /> New Case
            </button>
          </div>
        </div>

        {/* Tabs: My Cases / Shared With Me */}
        {sharedCases.length > 0 && (
          <div className="flex border-b border-[#E5E7EB] mb-4">
            <button onClick={() => setTab('my')} data-testid="tab-my-cases"
              className={`px-4 py-2 text-sm font-ibm border-b-2 -mb-px transition-all ${tab === 'my' ? 'border-[#002FA7] text-[#002FA7] font-semibold' : 'border-transparent text-[#6B7280]'}`}>
              My Cases ({cases.length})
            </button>
            <button onClick={() => setTab('shared')} data-testid="tab-shared-cases"
              className={`px-4 py-2 text-sm font-ibm border-b-2 -mb-px transition-all flex items-center gap-1 ${tab === 'shared' ? 'border-[#002FA7] text-[#002FA7] font-semibold' : 'border-transparent text-[#6B7280]'}`}>
              <UsersThree size={14} /> Shared With Me ({sharedCases.length})
            </button>
          </div>
        )}

        <div className="relative mb-6">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input data-testid="case-search-input" type="text" placeholder="Search cases..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-[#E5E7EB] rounded-md bg-white text-[#09090B] font-ibm focus:outline-none focus:ring-2 focus:ring-[#002FA7] transition-all" />
        </div>

        {renderCaseList()}

        {showCreate && (
          <CreateCaseModal newCase={newCase} setNewCase={setNewCase} createCase={createCase} creating={creating} onClose={() => setShowCreate(false)} />
        )}
        {showTemplates && (
          <TemplatesModal templates={templates} createFromTemplate={createFromTemplate} creating={creating} onClose={() => setShowTemplates(false)} />
        )}
      </div>
    </AppLayout>
  );
}
